import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import AccountLayout from "./AccountLayout";
import {
  useAddresses,
  useCreateAddress,
  useUpdateAddress,
  useDeleteAddress,
  useSetDefaultAddress,
} from "../libs/useAddresses";
import { NIGERIAN_STATES } from "../libs/categoryRoutes";
import { AccountAddressesSkeleton } from "./AccountSkeletons";
import "./UserProfile.css";

const emptyForm = {
  label: "Home",
  full_name: "",
  email: "",
  phone_number: "",
  second_phone: "",
  city: "",
  state: "Lagos",
  address: "",
  isDefault: false,
};

const Addressbook = () => {
  const { data: addresses = [], isLoading } = useAddresses();
  const { mutate: createAddress } = useCreateAddress();
  const { mutate: updateAddress } = useUpdateAddress();
  const { mutate: deleteAddress } = useDeleteAddress();
  const { mutate: setDefault } = useSetDefaultAddress();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (addr) => {
    setEditingId(addr._id);
    setForm({
      label: addr.label || "Home",
      full_name: addr.full_name || "",
      email: addr.email || "",
      phone_number: addr.phone_number || "",
      second_phone: addr.second_phone || "",
      city: addr.city || "",
      state: addr.state || "Lagos",
      address: addr.address || "",
      isDefault: !!addr.isDefault,
    });
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingId) {
      updateAddress(
        { id: editingId, data: form },
        {
          onSuccess: () => {
            toast.success("Address updated");
            setShowForm(false);
            setEditingId(null);
            setForm(emptyForm);
          },
          onError: () => toast.error("Could not update address"),
        }
      );
    } else {
      createAddress(form, {
        onSuccess: () => {
          toast.success("Address saved");
          setShowForm(false);
          setForm(emptyForm);
        },
        onError: () => toast.error("Could not save address"),
      });
    }
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this address?")) return;
    deleteAddress(id, {
      onSuccess: () => toast.success("Address deleted"),
      onError: () => toast.error("Could not delete address"),
    });
  };

  if (isLoading) {
    return (
      <AccountLayout loading>
        <AccountAddressesSkeleton />
      </AccountLayout>
    );
  }

  return (
    <AccountLayout title="Address Book">
      {addresses.length === 0 && !showForm && (
        <div className="account-empty">
          <h3>No saved addresses</h3>
          <p>Add an address for faster checkout.</p>
          <button type="button" className="account-btn account-btn--primary" onClick={openCreate}>
            Add address
          </button>
        </div>
      )}

      <div className="address-list">
        {addresses.map((addr) => (
          <div key={addr._id} className="address-card">
            <strong>
              {addr.label}
              {addr.isDefault && " (Default)"}
            </strong>
            <p>
              {addr.full_name} — {addr.phone_number}
            </p>
            <p>
              {addr.address}, {addr.city}, {addr.state}
            </p>
            <div className="order-card-actions">
              <button type="button" onClick={() => openEdit(addr)}>
                Edit
              </button>
              {!addr.isDefault && (
                <button type="button" onClick={() => setDefault(addr._id)}>
                  Set default
                </button>
              )}
              <button type="button" className="order-action--danger" onClick={() => handleDelete(addr._id)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {showForm ? (
        <form className="address-form" onSubmit={handleSubmit}>
          <input
            placeholder="Label (e.g. Home, Office)"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
          />
          <input
            placeholder="Full name"
            required
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
          />
          <input
            placeholder="Email"
            required
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <input
            placeholder="Phone"
            required
            value={form.phone_number}
            onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
          />
          <input
            placeholder="Second phone (optional)"
            value={form.second_phone}
            onChange={(e) => setForm({ ...form, second_phone: e.target.value })}
          />
          <input
            placeholder="City"
            required
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
          />
          <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}>
            {NIGERIAN_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <input
            placeholder="Street address"
            required
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
          <label>
            <input
              type="checkbox"
              checked={form.isDefault}
              onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
            />
            Set as default address
          </label>
          <button type="submit" className="account-btn account-btn--primary">
            {editingId ? "Update address" : "Save address"}
          </button>
          <button
            type="button"
            className="account-btn account-btn--ghost"
            onClick={() => {
              setShowForm(false);
              setEditingId(null);
            }}
          >
            Cancel
          </button>
        </form>
      ) : (
        addresses.length > 0 && (
          <button type="button" className="account-btn account-btn--primary add-address-btn" onClick={openCreate}>
            Add new address
          </button>
        )
      )}

      <Link to="/account" className="account-back-link">
        ← Back to profile
      </Link>
    </AccountLayout>
  );
};

export default Addressbook;
